import type { MapDataLocationFeature, MapIconFonts } from './map-types';
import { getFontInfoForGlyph, getIconForGlyph } from './map-utils';

export const MARKER_HEIGHT = 56;
export const MARKER_WIDTH = 47;

const ARROW_HEIGHT = 5;
const ARROW_HALF_WIDTH = 4;
const PIN_CIRCLE_TOP = 8;
const ARROW_MARGIN_ABOVE_PIN = 2;
const ARROW_BOTTOM_Y = PIN_CIRCLE_TOP - ARROW_MARGIN_ABOVE_PIN;
const ARROW_TOP_Y = ARROW_BOTTOM_Y - ARROW_HEIGHT;

const GENERATED_MARKER_IMAGES: Record<string, string> = {};

// Mirrors green.500 in packages/panda-config/src/tokens/colors/palette.ts — the SVG is
// inlined as a data URL and can't reference the token, so keep this in sync if it moves.
const HIGHLIGHTED_MARKER_BG = '#ddf00c';
const HIGHLIGHTED_MARKER_FG = '#000000';

interface MapMarkerConfig {
    readonly lat: number;
    readonly lon: number;
    readonly icon: string;
    readonly color: string;
    location: MapDataLocationFeature;
    externalId: string;
    fonts: MapIconFonts;
    width?: number;
    height?: number;
    highlighted?: boolean;
}

interface SvgIconInfo {
    iconSvgPath: string;
    bitmap?: {
        dataUrl: string;
        isMask: boolean;
    };
    bbox: {
        width: number;
        height: number;
        minX: number;
        minY: number;
    };
}

interface BitmapGlyphImage {
    readonly data: Uint8Array;
    readonly type: string;
}

interface BitmapGlyph {
    getImageForSize(size: number): BitmapGlyphImage | null;
}

function getBitmapIcon(glyph: unknown): SvgIconInfo['bitmap'] {
    if (!glyph || typeof glyph !== 'object' || !('getImageForSize' in glyph)) return undefined;

    const bitmapGlyph = glyph as BitmapGlyph;
    const image = bitmapGlyph.getImageForSize(128);
    if (!image || image.type.trim() !== 'png') return undefined;

    return {
        dataUrl: `data:image/png;base64,${Buffer.from(image.data).toString('base64')}`,
        // Generated white masks use PNG grayscale+alpha (color type 4); source-color
        // strikes use RGB/RGBA. This keeps the font self-describing without game IDs.
        isMask: image.data[25] === 4,
    };
}

export type VerticalLevel = 'above' | 'below' | null;

export class MapMarker {
    public readonly location: MapDataLocationFeature;
    public markerWidth: number;
    public markerHeight: number;
    public color: string;
    public externalId: string;
    public icon: string;
    public verticalLevel: VerticalLevel;
    public highlighted: boolean;

    config: MapMarkerConfig;

    public constructor(config: MapMarkerConfig) {
        this.icon = config.icon;
        this.color = config.color;
        this.config = config;
        this.location = config.location;
        this.externalId = config.externalId;
        this.verticalLevel = config.location.properties.verticalLevel ?? null;
        this.markerWidth = config.width ?? MARKER_WIDTH;
        this.markerHeight = config.height ?? MARKER_HEIGHT;
        this.highlighted = config.highlighted ?? false;
    }

    public get subcategoryExternalId(): string {
        return this.location?.properties.subcategoryExternalId ?? '';
    }

    public setFound(found: boolean): void {
        this.location.properties.found = found;
    }

    public get selected(): boolean {
        return !!this.location.properties.selected;
    }

    public get found(): boolean {
        return !!this.location.properties.found;
    }

    public select(): void {
        this.location.properties.selected = true;
    }

    public unselect(): void {
        this.location.properties.selected = false;
    }

    public setIcon(icon: string): void {
        this.icon = icon;
    }

    public get iconSvg(): string {
        const comboKey = `${this.color}-${this.selected}-${this.found}-${this.icon}-${this.verticalLevel ?? 'none'}-${this.highlighted ? 'hl' : 'nl'}`;

        if (GENERATED_MARKER_IMAGES[comboKey]) {
            return GENERATED_MARKER_IMAGES[comboKey];
        }

        const fontInfo = getFontInfoForGlyph(this.icon);
        const glyphPrefix =
            fontInfo.glyphPrefix && !this.icon.startsWith(fontInfo.glyphPrefix) ? fontInfo.glyphPrefix : '';
        const glyphName = getIconForGlyph(`${glyphPrefix}${this.icon}`);
        const font = this.config.fonts[fontInfo.fontName]?.layout(glyphName);

        const glyph = font?.glyphs?.[0];
        const glyphIconSvg = glyph?.path?.toSVG?.() ?? '';
        const bitmap = getBitmapIcon(glyph);
        const glyphBBox = glyph?.path?.bbox ?? { width: 0, height: 0, minX: 0, minY: 0 };
        const hasGlyph = glyphIconSvg !== '' || bitmap !== undefined;

        const svg = this.makeMapMarkerSVG(this.color, this.selected, this.found, {
            iconSvgPath: glyphIconSvg,
            bitmap,
            bbox: {
                width: glyphBBox.width,
                height: glyphBBox.height,
                minX: glyphBBox.minX,
                minY: glyphBBox.minY,
            },
        });

        // Only cache if the glyph was resolved — avoids persisting broken
        // markers when fonts haven't finished loading yet.
        if (hasGlyph) {
            GENERATED_MARKER_IMAGES[comboKey] = svg;
        }

        return svg;
    }

    makeMapMarkerSVG(color: string, selected: boolean, found: boolean, svgIconInfo: SvgIconInfo): string {
        const iconSize = 16;
        const hasArrow = !!this.verticalLevel;
        const styles = `
            filter: brightness(${found ? 0.5 : 1});
        `;

        const scaleX = iconSize / svgIconInfo.bbox.width;
        const scaleY = iconSize / svgIconInfo.bbox.height;
        const scale = Math.min(scaleX, scaleY);

        // Font coordinate origins vary: the generic fonts sit on a zero baseline while
        // game fonts can include a descender. Center from the actual glyph bounds so both
        // kinds occupy the same 16px box.
        const centerX = svgIconInfo.bbox.minX + svgIconInfo.bbox.width / 2;
        const centerY = svgIconInfo.bbox.minY + svgIconInfo.bbox.height / 2;
        const translateX = iconSize / 2 - centerX * scale;
        const translateY = iconSize / 2 + centerY * scale;

        const pinFill = this.highlighted ? HIGHLIGHTED_MARKER_BG : selected ? 'white' : color;
        const pinStroke = this.highlighted ? 'none' : 'white';
        const glyphFill = this.highlighted ? HIGHLIGHTED_MARKER_FG : selected ? color : 'white';
        const tintedBitmap =
            svgIconInfo.bitmap && (svgIconInfo.bitmap.isMask || selected || this.highlighted)
                ? svgIconInfo.bitmap
                : null;

        return `
        <svg style="${styles}" viewBox="0 0 ${this.markerWidth} ${this.markerHeight}" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <filter id="foundIconShadow">
                    <feDropShadow dx="0" dy="1" stdDeviation="1" flood-opacity="0.3"/>
                </filter>
                ${
                    tintedBitmap
                        ? `<mask id="glyphMask" mask-type="alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="${iconSize}" height="${iconSize}">
                    <image href="${tintedBitmap.dataUrl}" width="${iconSize}" height="${iconSize}" preserveAspectRatio="xMidYMid meet" />
                </mask>`
                        : ''
                }
            </defs>

            ${hasArrow ? this.getVerticalLevelArrowSvg() : ''}

            <g>
                <path d="M36 20.5C36 30.0422 30.8938 38.2395 23.5756 41.9775C16.1663 38.2386 11 30.04 11 20.5C11 13.5964 16.5964 8 23.5 8C30.4036 8 36 13.5964 36 20.5Z"
                    fill="${pinFill}"
                    stroke="${pinStroke}"
                />

                <g transform="translate(${iconSize}, ${iconSize})">
                    ${
                        tintedBitmap
                            ? `<rect width="${iconSize}" height="${iconSize}" fill="${glyphFill}" mask="url(#glyphMask)" />`
                            : svgIconInfo.bitmap
                              ? `<image href="${svgIconInfo.bitmap.dataUrl}" width="${iconSize}" height="${iconSize}" preserveAspectRatio="xMidYMid meet" />`
                              : `<g transform="translate(${translateX}, ${translateY}) scale(${scale}, ${-scale})">
                        <path d="${svgIconInfo.iconSvgPath}" fill="${glyphFill}" />
                    </g>`
                    }
                </g>

                ${found ? this.getFoundIconSvg(color) : ''}
            </g>
        </svg>`;
    }

    getVerticalLevelArrowSvg(): string {
        const cx = this.markerWidth / 2;
        if (this.verticalLevel === 'above') {
            return `<polygon points="${cx - ARROW_HALF_WIDTH},${ARROW_BOTTOM_Y} ${cx + ARROW_HALF_WIDTH},${ARROW_BOTTOM_Y} ${cx},${ARROW_TOP_Y}" fill="white" />`;
        }
        return `<polygon points="${cx - ARROW_HALF_WIDTH},${ARROW_TOP_Y} ${cx + ARROW_HALF_WIDTH},${ARROW_TOP_Y} ${cx},${ARROW_BOTTOM_Y}" fill="white" />`;
    }

    getFoundIconSvg(color: string): string {
        return `
            <g transform="translate(28, 4)" filter="url(foundIconShadow)">
                <circle cx="8" cy="8" r="8" fill="white" />
                <path d="M12 5L6.5 10.5L4 8L5 7L6.5 8.5L11 4L12 5Z" fill="${color}" />
            </g>`;
    }
}
